<template>
  <section class="exit-chat-section">
    <div class="section-heading">
      <div>
        <h3>User Exit Chats</h3>
        <p>Optional feedback shared by App Clients while closing their accounts.</p>
      </div>

      <div class="heading-actions">
        <span class="chat-count-pill">{{ exitChats.length }} Chats</span>
        <button type="button" class="refresh-button" :disabled="loading" @click="loadExitChats">
          {{ loading ? "Loading..." : "Refresh" }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-card">Loading User Exit Chats...</div>
    <div v-else-if="errorMessage" class="state-card error">{{ errorMessage }}</div>
    <div v-else-if="exitChats.length === 0" class="state-card">
      No App Clients have submitted optional exit feedback.
    </div>

    <div v-else class="exit-chat-list">
      <article v-for="chat in exitChats" :key="chat.id" class="exit-chat-card">
        <button type="button" class="exit-chat-row" @click="toggleChat(chat)">
          <div class="client-identity">
            <span class="row-label">App Client</span>
            <strong>{{ chat.authEmail || chat.notificationEmail || "Email unavailable" }}</strong>
            <small>{{ chat.uid || chat.id }}</small>
          </div>

          <div class="feedback-preview">
            <span class="row-label">Feedback</span>
            <strong>{{ chat.exitFeedbackPreview || "Feedback submitted" }}</strong>
          </div>

          <div class="chat-meta">
            <span class="message-pill">{{ chat.exitFeedbackMessageCount || 0 }} Client Message(s)</span>
            <span v-if="chat.hasOpenMatters" class="open-matter-pill">
              {{ chat.openMatterCount || 0 }} Open Matter(s)
            </span>
            <time>{{ formatDate(chat.exitFeedbackLastAt) }}</time>
          </div>

          <span class="material-icons-outlined chevron" aria-hidden="true">
            {{ selectedChatId === chat.id ? "expand_less" : "expand_more" }}
          </span>
        </button>

        <div v-if="selectedChatId === chat.id" class="transcript-panel">
          <div v-if="loadingMessages" class="transcript-state">Loading feedback conversation...</div>
          <div v-else-if="messageError" class="transcript-state error">{{ messageError }}</div>
          <div v-else-if="selectedMessages.length === 0" class="transcript-state">
            The archived feedback transcript is unavailable.
          </div>
          <div v-else class="transcript">
            <div
              v-for="message in selectedMessages"
              :key="message.id"
              class="message"
              :class="message.senderRole"
            >
              <span>{{ message.senderRole === "client" ? "App Client" : "Maix" }}</span>
              <p>{{ message.messageText }}</p>
              <time>{{ formatDateTime(message.sentAt) }}</time>
            </div>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { onMounted, ref, watch } from "vue";

import {
  fetchUserExitChatMessages,
  fetchUserExitChats
} from "@/firebase/firestore";

export default {
  name: "UserExitChats",

  props: {
    refreshKey: {
      type: Number,
      default: 0
    }
  },

  setup(props) {
    const exitChats = ref([]);
    const loading = ref(false);
    const errorMessage = ref("");
    const selectedChatId = ref("");
    const selectedMessages = ref([]);
    const loadingMessages = ref(false);
    const messageError = ref("");

    const loadExitChats = async () => {
      loading.value = true;
      errorMessage.value = "";

      try {
        exitChats.value = await fetchUserExitChats({ maxResults: 100 });
      } catch (error) {
        console.error("❌ Unable to load User Exit Chats:", error);
        errorMessage.value = error?.message ?? "Unable to load User Exit Chats.";
      } finally {
        loading.value = false;
      }
    };

    const toggleChat = async chat => {
      if (selectedChatId.value === chat.id) {
        selectedChatId.value = "";
        selectedMessages.value = [];
        return;
      }

      selectedChatId.value = chat.id;
      selectedMessages.value = [];
      loadingMessages.value = true;
      messageError.value = "";

      try {
        selectedMessages.value = await fetchUserExitChatMessages(chat);
      } catch (error) {
        console.error("❌ Unable to load User Exit Chat transcript:", error);
        messageError.value = error?.message ?? "Unable to load the exit feedback transcript.";
      } finally {
        loadingMessages.value = false;
      }
    };

    const dateValue = value => value?.toDate ? value.toDate() : new Date(value ?? 0);
    const formatDate = value => {
      if (!value) { return "—"; }
      const date = dateValue(value);
      return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric"
      });
    };
    const formatDateTime = value => {
      if (!value) { return ""; }
      const date = dateValue(value);
      return Number.isNaN(date.getTime()) ? "" : date.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
      });
    };

    onMounted(loadExitChats);
    watch(() => props.refreshKey, loadExitChats);

    return {
      exitChats,
      loading,
      errorMessage,
      selectedChatId,
      selectedMessages,
      loadingMessages,
      messageError,
      loadExitChats,
      toggleChat,
      formatDate,
      formatDateTime
    };
  }
};
</script>

<style scoped>
.exit-chat-section { display: grid; gap: 12px; margin-top: 26px; padding-top: 26px; border-top: 1px solid #dfe5e1; }
.section-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; }
.section-heading h3 { margin: 0; color: #1f2d26; font-size: 1rem; font-weight: 600; }
.section-heading p { margin: 4px 0 0; color: #7b8580; font-size: 0.84rem; }
.heading-actions { display: flex; align-items: center; gap: 9px; }
.chat-count-pill, .message-pill, .open-matter-pill { padding: 5px 9px; border-radius: 999px; background: #edf7f1; color: #178554; font-size: 0.72rem; font-weight: 700; white-space: nowrap; }
.open-matter-pill { background: #fff0e8; color: #a35220; }
.refresh-button { padding: 7px 11px; border: 1px solid #d8dfdb; border-radius: 8px; background: #fff; color: #35423c; font: inherit; font-size: 0.78rem; font-weight: 700; cursor: pointer; }
.refresh-button:disabled { opacity: 0.6; cursor: default; }
.state-card { padding: 22px; border: 1px solid #e0e5e2; border-radius: 12px; background: #f8faf9; color: #64716b; text-align: center; }
.state-card.error, .transcript-state.error { border-color: #e6bcbc; background: #fff6f6; color: #9d3535; }
.exit-chat-list { display: grid; gap: 10px; }
.exit-chat-card { overflow: hidden; border: 1px solid #dfe5e1; border-radius: 12px; background: #f8faf9; }
.exit-chat-row { display: grid; grid-template-columns: minmax(220px, 1.2fr) minmax(260px, 2fr) minmax(210px, 1fr) 24px; align-items: center; gap: 18px; width: 100%; padding: 16px 18px; border: 0; background: transparent; color: #2f3c35; font: inherit; text-align: left; cursor: pointer; }
.exit-chat-row:hover { background: #f3f7f5; }
.client-identity, .feedback-preview { display: grid; gap: 4px; min-width: 0; }
.row-label { color: #8a948f; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
.client-identity strong, .client-identity small, .feedback-preview strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.client-identity small { color: #7f8984; font-size: 0.7rem; }
.feedback-preview strong { font-size: 0.9rem; }
.chat-meta { display: flex; align-items: flex-end; flex-direction: column; gap: 6px; }
.chat-meta time { color: #7f8984; font-size: 0.76rem; }
.chevron { color: #87918c; }
.transcript-panel { padding: 18px; border-top: 1px solid #dfe5e1; background: #fff; }
.transcript-state { padding: 14px; border-radius: 9px; background: #f4f7f5; color: #6e7a74; text-align: center; }
.transcript { display: grid; gap: 12px; }
.message { width: min(78%, 720px); padding: 11px 13px; border-radius: 12px; background: #eaf4ef; }
.message.client { justify-self: end; background: #e8f1f8; }
.message > span { color: #61716a; font-size: 0.68rem; font-weight: 750; text-transform: uppercase; }
.message p { margin: 5px 0 7px; line-height: 1.45; white-space: pre-wrap; }
.message time { color: #7d8883; font-size: 0.7rem; }
@media (max-width: 1050px) {
  .exit-chat-row { grid-template-columns: minmax(200px, 1fr) minmax(240px, 1.5fr) 24px; }
  .chat-meta { display: none; }
}
@media (max-width: 720px) {
  .section-heading { align-items: flex-start; flex-direction: column; }
  .exit-chat-row { grid-template-columns: minmax(0, 1fr) 24px; }
  .feedback-preview { grid-column: 1; }
  .chevron { grid-column: 2; grid-row: 1 / span 2; }
  .message { width: 88%; }
}
</style>
